import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Admin from './pages/Admin';
import NajSportLogin from './pages/NajSportLogin';
import NajSportEvents from './pages/NajSportEvents';
import NajSportProfile from './pages/NajSportProfile';
import NajSportOnboarding from './pages/NajSportOnboarding';
import NajSportLeaderboard from './pages/NajSportLeaderboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import UserProfile from './pages/UserProfile';
import GdprConsentModal from './components/GdprConsentModal';
import { TenantProvider } from './context/TenantContext';
import './index.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const KEEP_ALIVE_INTERVAL = 5 * 60 * 1000; // 5 minutes

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const NajSportGuard: React.FC<{ children: React.ReactNode; requireOnboarding?: boolean }> = ({ children, requireOnboarding = true }) => {
  const stored = localStorage.getItem('najsportUser');
  const token = localStorage.getItem('token');
  const [gdprAccepted, setGdprAccepted] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    if (stored) {
      const u = JSON.parse(stored);
      setGdprAccepted(u.gdprConsent === true);
    }
  }, [stored]);

  if (!stored || !token) return <Navigate to="/" replace />;

  const u = JSON.parse(stored);
  if (requireOnboarding && !u.onboardingCompleted) return <Navigate to="/onboarding" replace />;

  // Show GDPR consent modal if not yet accepted
  if (gdprAccepted === false) {
    return (
      <GdprConsentModal
        onAccept={() => {
          // Update localStorage so guard passes on next render
          const current = JSON.parse(localStorage.getItem('najsportUser') || '{}');
          current.gdprConsent = true;
          localStorage.setItem('najsportUser', JSON.stringify(current));
          setGdprAccepted(true);
        }}
      />
    );
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<NajSportLogin />} />
        <Route path="/login" element={<NajSportLogin />} />
        <Route
          path="/onboarding"
          element={
            <NajSportGuard requireOnboarding={false}>
              <NajSportOnboarding />
            </NajSportGuard>
          }
        />
        <Route
          path="/events"
          element={
            <NajSportGuard>
              <NajSportEvents />
            </NajSportGuard>
          }
        />
        <Route
          path="/profile"
          element={
            <NajSportGuard>
              <NajSportProfile />
            </NajSportGuard>
          }
        />
        <Route
          path="/user/:userId"
          element={
            <NajSportGuard>
              <UserProfile />
            </NajSportGuard>
          }
        />
        <Route
          path="/leaderboard"
          element={
            <NajSportGuard>
              <NajSportLeaderboard />
            </NajSportGuard>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <Admin />
            </ProtectedRoute>
          }
        />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

function App() {
  useEffect(() => {
    const ping = () => fetch(`${API_URL}/health`).catch(() => {});
    const id = setInterval(ping, KEEP_ALIVE_INTERVAL);
    return () => clearInterval(id);
  }, []);

  return (
    <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
        <Router>
          <TenantProvider>
            <AppRoutes />
          </TenantProvider>
        </Router>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
